import { RNG } from "rot-js";
import { Bounds } from "../../../engine/space/bounds";
import { shuffle } from "../../../lib/shuffle";
import { ActorBreed } from "../../actors/Actor";
import { BreedTypes } from "../../actors/breeds";
import EntityManager from "../../EntityManager";
import { ItemTypes } from "../../items/itemsTypes";
import { GraphNode } from "../graph/buildMinSpanTree";
import { ShrineMap } from "../ShrineMap";
import { getRandomInBounds } from "./placement";

type SpawnOption = {
  min: number;
  max: number;
  breed: ActorBreed;
};

type Encounter = {
  readonly cost: number;
  readonly monsters: readonly SpawnOption[];
  readonly iChance: number;
};

const encounters: Record<string, Encounter> = {
  none: {
    cost: 0,
    monsters: [],
    iChance: 0.15,
  },
  faithlessBasic: {
    cost: 1,
    monsters: [{ min: 1, max: 2, breed: BreedTypes.faithless }],
    iChance: 0.75,
  },
  faithlessDense: {
    cost: 1,
    monsters: [{ min: 2, max: 3, breed: BreedTypes.faithless }],
    iChance: 1.0,
  },
  faithlessHeretic: {
    cost: 2,
    monsters: [
      { min: 2, max: 2, breed: BreedTypes.heretic },
      { min: 1, max: 1, breed: BreedTypes.faithless },
    ],
    iChance: 1.0,
  },
  simpleHeretic: {
    cost: 2,
    monsters: [{ min: 1, max: 2, breed: BreedTypes.heretic }],
    iChance: 0.25,
  },
  defilerSimple: {
    cost: 4,
    monsters: [{ min: 1, max: 1, breed: BreedTypes.defiler }],
    iChance: 0.5,
  },
  defilerDense: {
    cost: 4,
    monsters: [{ min: 2, max: 2, breed: BreedTypes.defiler }],
    iChance: 0.5,
  },
  apostateSimple: {
    cost: 4,
    monsters: [{ min: 1, max: 1, breed: BreedTypes.apostate }],
    iChance: 0.75,
  },
  apostateDefiler: {
    cost: 4,
    monsters: [
      { min: 1, max: 1, breed: BreedTypes.defiler },
      { min: 1, max: 1, breed: BreedTypes.apostate },
    ],
    iChance: 0.75,
  },
  apostateDense: {
    cost: 4,
    monsters: [{ min: 2, max: 2, breed: BreedTypes.apostate }],
    iChance: 0.75,
  },
  simpleProphet: {
    cost: 4,
    monsters: [
      { min: 1, max: 1, breed: BreedTypes.falseProphet },
      { min: 2, max: 5, breed: BreedTypes.cultist },
    ],
    iChance: 1.0,
  },
} as const;

const depthWeights: Partial<{ [key in keyof typeof encounters]: number }>[] = [
  {
    none: 26,
    faithlessBasic: 20,
    faithlessDense: 5,
  },
  {
    none: 40,
    faithlessDense: 5,
    hereticSimple: 10,
    faithlessHeretic: 20,
    defilerSimple: 5,
  },
  {
    none: 40,
    faithlessHeretic: 20,
    defilerSimple: 20,
    defilerDense: 2,
    apostateSimple: 5,
  },
  {
    none: 40,
    faithlessHeretic: 15,
    defilerSimple: 10,
    defilerDense: 5,
    apostateDefiler: 5,
    apostateDense: 5,
    simpleProphet: 5,
  },
  {
    none: 18,
    simpleProphet: 25,
    defilerDense: 3,
    apostateDefiler: 3,
    apostateDense: 3,
  },
];

export const placeMonsters = (
  rng: typeof RNG,
  map: ShrineMap,
  graph: Map<number, GraphNode>,
  starting: Bounds
) => {
  let used = 0;
  const spawnLocs: Record<number, boolean> = {};
  let iType: string;
  let quantity: number;

  let iChoices = [];
  for ([iType, quantity] of map.lootTable.entries()) {
    for (let i = 0; i < quantity; i++) {
      iChoices.push(iType);
    }
  }

  iChoices = shuffle(iChoices);

  for (const room of graph.values()) {
    if (!room || room.bounds === starting) {
      continue;
    }
    const encounterType = rng.getWeightedValue(
      (depthWeights[map.depth - 1] ||
        depthWeights[depthWeights.length - 1]) as any
    ) as keyof typeof encounters;
    if (encounterType) {
      const encounter = encounters[encounterType];
      if (encounter) {
        used += encounter.cost;
        encounter.monsters.forEach((spawn) => {
          const total = rng.getUniformInt(spawn.min, spawn.max);
          for (let i = 0; i < total; i++) {
            for (let fail = 0; fail < 100; fail++) {
              const pos = getRandomInBounds(rng, room.bounds);
              if (!spawnLocs[pos.x + pos.y * map.width]) {
                spawnLocs[pos.x + pos.y * map.width] = true;
                EntityManager.createActor(spawn.breed, pos.x, pos.y);
                break;
              }
            }
          }
        });

        //Item Spawn
        if (iChoices.length && rng.getUniform() <= encounter.iChance) {
          const pos = getRandomInBounds(rng, room.bounds);
          EntityManager.createItem(ItemTypes[iChoices.pop()], pos.x, pos.y);
        }
      }
    }
  }
};
